import React, { createContext, useEffect, useReducer } from 'react';
import { mutate } from 'swr';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project imports
import Loader from 'components/Loader';
import axios from 'utils/axios';
import { AuthProps, JWTContextType, LoginResponse, LoginResponse2FA } from 'types/auth';

import { saveTenantUniqueCode, clearTenantUniqueCode } from 'utils/tenantRouting';

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const setSession = (user: any) => {
  if (user) {
    localStorage.setItem('currentUser', JSON.stringify(user));
    if (user?.userType === 1) {
      clearTenantUniqueCode();
    } else if (user?.uniqueCode) {
      saveTenantUniqueCode(user.uniqueCode);
    }
  } else {
    localStorage.removeItem('currentUser');
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const path = window.location.pathname;

  useEffect(() => {
    const init = async () => {
      try {
        const savedUser = window.localStorage.getItem('currentUser');
        if (savedUser && path !== '/forgot-password' && path !== '/reset-password') {
          const user = JSON.parse(savedUser);
          if (user?.userType === 1) {
            clearTenantUniqueCode();
          } else if (user?.uniqueCode) {
            saveTenantUniqueCode(user.uniqueCode);
          }
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user
            }
          });
        } else {
          setSession(null);
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        setSession(null);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  // Multi-tab synchronization (Auto-Logout, Auto-Login & Account Switch across tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'currentUser') {
        if (!e.newValue && state.isLoggedIn) {
          // Session was removed in another tab -> sync logout immediately
          dispatch({ type: LOGOUT });
        } else if (e.newValue) {
          try {
            const currentPath = window.location.pathname;
            // Avoid interrupting reset/forgot password forms if user is on those pages
            if (currentPath.includes('/forgot-password') || currentPath.includes('/reset-password')) {
              return;
            }

            const newUser = JSON.parse(e.newValue);
            if (newUser?.userType === 1) {
              clearTenantUniqueCode();
            } else if (newUser?.uniqueCode) {
              saveTenantUniqueCode(newUser.uniqueCode);
            }

            // Case 1: Tab was logged out -> Auto-login smoothly without flicker (handled by GuestGuard)
            if (!state.isLoggedIn) {
              dispatch({
                type: LOGIN,
                payload: {
                  isLoggedIn: true,
                  user: newUser
                }
              });

              // Revalidate SWR caches for fresh data
              try {
                mutate(() => true, undefined, { revalidate: true });
              } catch {}
            }
            // Case 2: Tab was already logged in, but user changed/switched in another tab
            else if (
              state.user?.userID !== newUser?.userID ||
              state.user?.tokenGeneratedTime !== newUser?.tokenGeneratedTime
            ) {
              dispatch({
                type: LOGIN,
                payload: {
                  isLoggedIn: true,
                  user: newUser
                }
              });

              try {
                mutate(() => true, undefined, { revalidate: true });
              } catch {}

              // Cleanly transition to new user's dashboard to avoid state/permission collision
              let targetDashboard = '/';
              if (newUser?.userType === 2) {
                targetDashboard = '/tenant-dashboard';
              } else if (newUser?.userType === 4) {
                targetDashboard = '/contractor-dashboard';
              }
              window.location.replace(targetDashboard);
            }
          } catch (err) {
            console.error('Failed to sync user session across tabs:', err);
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [state.isLoggedIn, state.user]);

  const login = async (email: string, password: string, checked: boolean, uniqueCode?: string): Promise<LoginResponse> => {
    const payload = {
      emailID: email,
      password,
      rememberMe: checked,
      ...(uniqueCode && { uniqueCode }) // Include uniqueCode if provided
    };
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await axios.post('/Auth/Login', payload, { headers: { 'X-TimeZone': timeZone } });
      const data = response.data;

      // Failed login attempt
      if (data.ok !== undefined && !data.ok) {
        return {
          ok: false,
          failedLoginCount: data.failedLoginCount,
          lockoutUntil: data.lockoutUntil,
          isPermanentlyLocked: data.isPermanentlyLocked
        };
      }
      // 2FA required
      if (data?.twoFAEnabled) {
        return {
          ok: true,
          twoFAEnabled: true,
          defaultAuthenticationType: data.defaultAuthenticationType,
          loginCode: data.loginCode,
          tokenGeneratedTime: data.tokenGeneratedTime
        };
      }

      const { userID, displayName, userRoleID, tenantID, companyName, expiresIn, tokenGeneratedTime, userType } = data;

      const user = {
        tenantID,
        companyName,
        displayName,
        expiresIn,
        userID,
        userRoleID,
        tokenGeneratedTime,
        userType,
        uniqueCode
      };

      setSession(user);
      dispatch({ type: LOGIN, payload: { isLoggedIn: true, user } });
      // Revalidate all SWR caches to avoid stale data from previous session
      try { await mutate(() => true, undefined, { revalidate: true }); } catch { }

      await axios.patch(`/Logs/LoginLog/UpdateLoginDateTime/${tokenGeneratedTime}`);
      return { ok: true, twoFAEnabled: false, ...data };
    } catch (error: any) {
      throw error;
    }
  };

  const logout = async () => {
    const currentUser = localStorage.getItem('currentUser');
    let parsedUser: any = null;
    try {
      parsedUser = currentUser ? JSON.parse(currentUser) : null;
    } catch {}
    if (parsedUser?.userType === 1) {
      clearTenantUniqueCode();
    } else if (parsedUser?.uniqueCode) {
      saveTenantUniqueCode(parsedUser.uniqueCode);
    }
    dispatch({ type: LOGOUT });
    try {
      if (parsedUser?.tokenGeneratedTime) {
        await axios.patch(`/Logs/LoginLog/UpdateLogoutDateTime/${parsedUser.tokenGeneratedTime}`).catch((error) => {
          console.error('Failed to update logout time:', error);
        });
      }
    } finally {
      setSession(null);
      // Clear SWR caches without triggering refetches to avoid API calls during logout
      try { await mutate(() => true, undefined, { revalidate: false, populateCache: false }); } catch { }
    }
  };

  const forgotPassword = async (email: string, uniqueCode?: string) => {
    let url = `/UserManagement/ForgotPassword/SendPasswordResetToken?EmailID=${encodeURIComponent(email)}`;
    if (uniqueCode) {
      url += `&UniqueCode=${uniqueCode}`;
    }
    const response = await axios.post(url,
      {},
      {
        headers: {
          'X-TimeZone': timeZone
        }
      }
    );
    return response.data;
  };

  const validateResetToken = async (token: string) => {
    const response = await axios.get(`/UserManagement/ForgotPassword/ValidatePasswordResetToken/${token}`);
    return response.data;
  };

  const resetPassword = async (password: string, token: string) => {
    const payload = {
      password: password,
      passwordResetToken: token
    };
    const response = await axios.post(`/UserManagement/ForgotPassword/ResetPassword`, payload,
      {
        headers: {
          'X-TimeZone': timeZone
        }
      }
    );
    return response.data;
  };

  const updateProfile = () => { };
  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  const verifyOTP = async (otp: string, loginCode: string, remeberMe: boolean, uniqueCode?: string): Promise<LoginResponse2FA> => {
    const payload = { otp: otp, loginCode: loginCode, remeberMe: remeberMe };
    try {
      const response = await axios.post('/Auth/VerifyOTP', payload, {
        headers: {
          'X-TimeZone': timeZone
        }
      });
      const data = response.data;

      if (data.ok !== undefined && !data.ok) {
        return {
          ok: false,
          otpFailedLoginCount: data.otpFailedLoginCount
        };
      }
      // 2FA required
      if (data?.twoFAEnabled) {
        return {
          ok: true,
          twoFAEnabled: true,
          defaultAuthenticationType: data.defaultAuthenticationType,
          loginCode: data.loginCode,
          tokenGeneratedTime: data.tokenGeneratedTime
        };
      }

      const { userID, displayName, userRoleID, tenantID, companyName, expiresIn, tokenGeneratedTime, userType } = data;

      const user = {
        tenantID,
        companyName,
        displayName,
        expiresIn,
        userID,
        userRoleID,
        tokenGeneratedTime,
        userType, // Include userType
        uniqueCode
      };

      setSession(user);
      dispatch({ type: LOGIN, payload: { isLoggedIn: true, user } });
      try { await mutate(() => true, undefined, { revalidate: true }); } catch { }

      await axios.patch(`/Logs/LoginLog/UpdateLoginDateTime/${tokenGeneratedTime}`);
      return { ok: true, twoFAEnabled: false, ...data };
    } catch (error: any) {
      throw error;
    }
  };

  const sendOTP = async (loginCode: string) => {
    const response = await axios.post(`/Auth/SendOTP/${loginCode}`, {}, {
      headers: {
        'X-TimeZone': timeZone
      }
    });
    return response.data;
  };


  const sendGoogleAuthenticatorSetupCode = async (loginCode: string) => {
    const response = await axios.post(`/Auth/SendGoogleAuthenticatorSetupCode/${loginCode}`, {}, {
      headers: {
        'X-TimeZone': timeZone
      }
    });
    return response.data;
  };

  const verifyGoogleAuthenticatorOTP = async (otp: string, loginCode: string, remeberMe: boolean, uniqueCode?: string): Promise<LoginResponse2FA> => {
    const payload = { otp: otp, loginCode: loginCode, remeberMe: remeberMe };
    try {
      const response = await axios.post('/Auth/VerifyGoogleAuthenticatorOTP', payload, {
        headers: {
          'X-TimeZone': timeZone
        }
      });
      const data = response.data;
      if (data.ok !== undefined && !data.ok) {
        return {
          ok: false,
          otpFailedLoginCount: data.otpFailedLoginCount
        };
      }
      // 2FA required
      if (data?.twoFAEnabled) {
        return {
          ok: true,
          twoFAEnabled: true,
          defaultAuthenticationType: data.defaultAuthenticationType,
          loginCode: data.loginCode,
          tokenGeneratedTime: data.tokenGeneratedTime
        };
      }

      // Normal login
      const { userID, displayName, userRoleID, tenantID, companyName, expiresIn, tokenGeneratedTime, userType } = data;

      const user = {
        tenantID,
        companyName,
        displayName,
        expiresIn,
        userID,
        userRoleID,
        tokenGeneratedTime,
        userType,
        uniqueCode
      };

      setSession(user);
      dispatch({ type: LOGIN, payload: { isLoggedIn: true, user } });
      try { await mutate(() => true, undefined, { revalidate: true }); } catch { }

      await axios.patch(`/Logs/LoginLog/UpdateLoginDateTime/${tokenGeneratedTime}`);
      return { ok: true, twoFAEnabled: false, ...data };
    } catch (error: any) {
      throw error;
    }
  };

  // End New APi End Point
  return (
    <JWTContext value={{ ...state, login, logout, forgotPassword, validateResetToken, resetPassword, updateProfile, verifyOTP, sendOTP, sendGoogleAuthenticatorSetupCode, verifyGoogleAuthenticatorOTP }}>
      {children}
    </JWTContext>
  );
};

export default JWTContext;
